import { useCallback } from 'react'
import {
  ActivityIndicator,
  FlatList,
  Modal,
  Platform,
  Pressable,
  StyleSheet,
  Text,
  View,
  type ListRenderItem,
} from 'react-native'
import { Check, Plus, Trash2, Users } from 'lucide-react-native'
import { useTheme } from '../../../theme/useTheme'
import { useAccountSwitcher, type SavedAccountSession } from '../../contexts/AccountSwitcherContext'
import { useAvatarContext } from '../../../avatars/contexts/AvatarContext'
import { WEB_HOVER_TRANSITION_STYLE } from '../../../shared/constants/animations'
import { FriendAvatar } from '../../../../components/friends/shared/FriendAvatar'
import type { DisplayAvatarId } from '../../../avatars/types/avatar.types'
import { useCloseModalThen } from '../../../shared/hooks/useCloseModalThen'

interface OrgSwitcherSheetProps {
  visible: boolean
  onClose: () => void
  /**
   * When the sheet sits inside another native Modal (the global menu), close that host too
   * and call `then` once it is gone, so the account switch never runs with a Modal on screen.
   */
  closeHostThen?: (then: () => void) => void
}

type Row = { kind: 'account'; account: SavedAccountSession } | { kind: 'add' }

/**
 * Account switcher sheet — shows all stored Zporti identities and an "Add account" button.
 * Tapping an account calls `supabase.auth.setSession()` and the whole app switches identity.
 */
export function OrgSwitcherSheet({ visible, onClose, closeHostThen }: OrgSwitcherSheetProps) {
  const theme = useTheme()
  const { accounts, activeUserId, loading, switchAccount, addAccount, removeAccount } =
    useAccountSwitcher()
  const { currentAvatarId, currentAvatarConfig } = useAvatarContext()

  // Switching accounts makes the next user's blocking prompts (age gate, Terms) present their
  // own Modal, so the switch waits until this sheet has finished closing (specs/profile-page, Story 8).
  const { closeThen, onModalDismiss } = useCloseModalThen({ onClose, closeHostThen })

  const handleSelect = useCallback(
    (userId: string) => {
      closeThen(() => {
        switchAccount(userId).catch(() => {
          /* switchAccount already logs; chip reverts automatically */
        })
      })
    },
    [closeThen, switchAccount],
  )

  const handleAddAccount = useCallback(() => {
    closeThen(() => {
      addAccount().catch(() => {
        // 'web_add_account_unsupported' and cancelled OAuth are non-fatal; nothing to show yet.
      })
    })
  }, [closeThen, addAccount])

  const handleRemove = useCallback(
    async (userId: string) => {
      try {
        await removeAccount(userId)
      } catch {
        /* non-fatal */
      }
    },
    [removeAccount],
  )

  const data: Row[] = [
    ...accounts.map((a): Row => ({ kind: 'account', account: a })),
    { kind: 'add' },
  ]

  const renderItem: ListRenderItem<Row> = ({ item }) => {
    if (item.kind === 'add') {
      return (
        <Pressable
          onPress={handleAddAccount}
          accessibilityRole="button"
          accessibilityLabel="Add another account"
          testID="account-switcher-add-account"
          style={({ pressed, hovered }) => [
            styles.row,
            { backgroundColor: 'transparent', borderColor: theme.gray[200] },
            Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
            pressed && styles.rowPressed,
            hovered && Platform.OS === 'web' && { backgroundColor: theme.background.cardElevated },
          ]}
        >
          <View
            style={[
              styles.avatar,
              { backgroundColor: theme.background.cardElevated, borderColor: theme.gray[200] },
            ]}
          >
            <Plus size={18} color={theme.primary} strokeWidth={2.4} />
          </View>
          <View style={styles.rowMeta}>
            <Text style={[styles.rowTitle, { color: theme.gray[900] }]}>Add account</Text>
            <Text style={[styles.rowSubtitle, { color: theme.gray[500] }]}>
              {Platform.OS === 'web'
                ? 'Sign in with another Google account'
                : 'Add another Google account'}
            </Text>
          </View>
        </Pressable>
      )
    }

    const { account } = item
    const isActive = account.userId === activeUserId
    // For the active account use the live avatarId from AvatarContext (no storage delay)
    const avatarId = (isActive
      ? (currentAvatarId ?? account.zportiAvatarId)
      : account.zportiAvatarId) as DisplayAvatarId | null
    // Active account uses the live custom look; others use whatever was last persisted for them.
    const avatarConfig = isActive
      ? (currentAvatarConfig ?? account.zportiAvatarConfig ?? null)
      : (account.zportiAvatarConfig ?? null)
    return (
      <Pressable
        onPress={() => handleSelect(account.userId)}
        accessibilityRole="button"
        accessibilityLabel={`Switch to ${account.displayName || account.email}`}
        accessibilityState={{ selected: isActive }}
        testID={`account-switcher-row-${account.userId.slice(0, 8)}`}
        style={({ pressed, hovered }) => [
          styles.row,
          {
            backgroundColor: isActive ? theme.background.cardElevated : 'transparent',
            borderColor: theme.gray[200],
          },
          Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
          pressed && styles.rowPressed,
          hovered && Platform.OS === 'web' && { backgroundColor: theme.background.cardElevated },
        ]}
      >
        <FriendAvatar
          avatarId={avatarId}
          avatarConfig={avatarConfig}
          fallbackUrl={account.avatarUrl ?? undefined}
          size={34}
          nameForInitials={account.displayName ?? account.email}
          avatarRounded
          small
        />
        <View style={styles.rowMeta}>
          <Text style={[styles.rowTitle, { color: theme.gray[900] }]} numberOfLines={1}>
            {account.displayName || account.email}
          </Text>
          {account.displayName ? (
            <Text style={[styles.rowSubtitle, { color: theme.gray[500] }]} numberOfLines={1}>
              {account.email}
            </Text>
          ) : null}
        </View>
        {isActive ? (
          <Check size={18} color={theme.success} strokeWidth={2.6} />
        ) : (
          <Pressable
            onPress={() => void handleRemove(account.userId)}
            hitSlop={8}
            accessibilityRole="button"
            accessibilityLabel={`Remove ${account.email} from this device`}
            style={({ pressed }) => [pressed && { opacity: 0.6 }]}
          >
            <Trash2 size={16} color={theme.gray[400]} strokeWidth={2} />
          </Pressable>
        )}
      </Pressable>
    )
  }

  return (
    <Modal
      transparent
      animationType="fade"
      visible={visible}
      onRequestClose={onClose}
      onDismiss={onModalDismiss}
      statusBarTranslucent
    >
      <Pressable
        style={[styles.backdrop, { backgroundColor: theme.background.overlay }]}
        onPress={onClose}
        accessibilityRole="button"
        accessibilityLabel="Close account switcher"
      >
        <Pressable
          onPress={() => {}}
          style={[
            styles.card,
            {
              backgroundColor: theme.background.card,
              borderColor: theme.gray[200],
              shadowColor: theme.shadow,
            },
            Platform.OS === 'web' ? styles.cardWeb : styles.cardNative,
          ]}
        >
          <View style={styles.cardHeader}>
            <Users size={18} color={theme.primary} strokeWidth={2.4} />
            <Text style={[styles.cardTitle, { color: theme.gray[900] }]}>Switch account</Text>
          </View>
          {loading && accounts.length === 0 ? (
            <View style={styles.loaderWrap}>
              <ActivityIndicator color={theme.accent} />
            </View>
          ) : (
            <FlatList
              data={data}
              renderItem={renderItem}
              keyExtractor={(item) =>
                item.kind === 'add' ? '__add__' : item.account.userId
              }
              ItemSeparatorComponent={() => <View style={{ height: 6 }} />}
              showsVerticalScrollIndicator={false}
            />
          )}
        </Pressable>
      </Pressable>
    </Modal>
  )
}

const styles = StyleSheet.create({
  backdrop: {
    flex: 1,
    justifyContent: Platform.OS === 'web' ? 'center' : 'flex-end',
    alignItems: 'center',
    padding: 16,
  },
  card: {
    width: '100%',
    maxHeight: '80%',
    borderRadius: 16,
    borderWidth: 1,
    padding: 16,
    shadowOpacity: 0.4,
    shadowRadius: 24,
    shadowOffset: { width: 0, height: 8 },
    elevation: 12,
  },
  cardWeb: { maxWidth: 420 },
  cardNative: { maxWidth: 520, marginBottom: 12 },
  cardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 12,
  },
  cardTitle: { fontSize: 16, fontWeight: '700' },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingVertical: 10,
    paddingHorizontal: 10,
    borderRadius: 12,
    borderWidth: 1,
  },
  avatar: {
    width: 34,
    height: 34,
    borderRadius: 17,
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  rowPressed: { opacity: 0.85 },
  rowMeta: { flex: 1, minWidth: 0 },
  rowTitle: { fontSize: 15, fontWeight: '600' },
  rowSubtitle: { fontSize: 12, marginTop: 2 },
  loaderWrap: { paddingVertical: 24, alignItems: 'center' },
})
